import { SK, kbd, titleCss, labelCss } from './skin';

// THE ONE window shell (oxide-shell-spec.md section 0). Every in-game window is this object: a glass
// panel of a fixed width with a 64px header (title block left, optional badge + close-key chip right),
// a 24px body, and a 44px footer that owns the interaction hints. Only the BODY content changes between
// windows. Build one with buildShell(), then fill shell.body via splitBody()/threeCol()/single padding.
//
// Every dimension here is a named constant resolved from oxide-tokens.json / oxide-shell-spec.md. No
// magic numbers: if a value is not one of these, it is a spec gap, not a choice.

const SHELL = {
  width: 1240,       // --oxw-width: fixed target for a 1920x1080 frame
  headH: 64,         // --oxw-head-h: exact, not min
  footMinH: 44,      // --oxw-foot-h
  paneLeft: 480,     // --oxw-pane-left: the split's fixed left pane
  pad: 24,           // --ox-s6: body + header-horizontal + pane padding
  footPadV: 10,      // footer vertical padding
  gap: 16,           // --ox-s4: title-to-content, pane-head margin
  vpMargin: 24,      // keep the window off the very screen edge on smaller displays
  hotbarReserve: 150, // bottom clearance so NO window ever overlaps the HUD hotbar (it stays a live drop target)
  // three-column body (crafting)
  colCats: 190,
  colDetail: 400,
} as const;

// Fixed close keys per window type (spec section 0). Do not vary.
export type CloseKey = 'TAB' | 'Q' | 'E';

export interface Shell {
  scrim: HTMLDivElement;   // full-screen backdrop (fixed inset:0); caller wires close-on-outside-tap
  win: HTMLDivElement;     // the glass window
  header: HTMLDivElement;
  body: HTMLDivElement;    // fill this; padded 24 unless bodyPad:false (split panes own their padding)
  footer: HTMLDivElement;
  hints: HTMLDivElement;   // footer-left hint bar
  setTitle(t: string): void;
  setSub(s: string | null): void;
  setBadge(html: string | null): void; // optional header badge, left of the close hint
}

export function buildShell(opts: { title: string; sub?: string; closeKey: CloseKey; bodyPad?: boolean; hintsHtml?: string }): Shell {
  const scrim = document.createElement('div');
  // z 13 matches every existing window backdrop; main.ts raises the HUD hotbar to z 30 above it so the
  // bar stays a reachable drop target. (The spec's z-token layering is deferred to avoid disturbing that.)
  // Bottom padding reserves the hotbar strip: a window can NEVER extend into it (owner 2026-07-13, the
  // crafting window was overlapping the bar).
  scrim.style.cssText = [
    'position:fixed', 'inset:0', 'z-index:13', 'display:none',
    'align-items:center', 'justify-content:center', 'box-sizing:border-box',
    `padding:${SHELL.vpMargin}px ${SHELL.vpMargin}px ${SHELL.hotbarReserve}px`,
    `background:${SK.scrim}`, `font:${SK.fsBody}/1.4 ${SK.font}`, `color:${SK.text}`,
  ].join(';');

  const win = document.createElement('div');
  win.dataset.ui = '1';
  win.style.cssText = [
    'display:flex', 'flex-direction:column', 'box-sizing:border-box', 'overflow:hidden',
    // 1240 on any real monitor; never overflow a narrow screen (spec's rem-scale lever comes later).
    `width:min(${SHELL.width}px, calc(100vw - ${SHELL.vpMargin * 2}px))`,
    `max-height:calc(100vh - ${SHELL.vpMargin + SHELL.hotbarReserve}px)`, // caps ABOVE the hotbar reserve
    `background:${SK.glass}`, `backdrop-filter:blur(${SK.blur})`, `-webkit-backdrop-filter:blur(${SK.blur})`,
    'border:1px solid rgba(255,255,255,0.07)', // shell-only hardcode (spec)
    `border-radius:${SK.rLg}`, 'box-shadow:0 24px 70px rgba(0,0,0,0.7)',
  ].join(';');

  // HEADER: title block left, actions (badge? + close hint) right.
  const header = document.createElement('div');
  header.style.cssText = [
    'display:flex', 'align-items:center', 'justify-content:space-between', 'gap:16px',
    `height:${SHELL.headH}px`, 'flex:none', `padding:0 ${SHELL.pad}px`, `border-bottom:1px solid ${SK.border}`,
  ].join(';');
  const tblock = document.createElement('div');
  const titleEl = document.createElement('div');
  titleEl.textContent = opts.title;
  titleEl.style.cssText = titleCss();
  const subEl = document.createElement('div');
  subEl.style.cssText = `font-size:${SK.fsMicro};color:${SK.dim};margin-top:2px;display:none`;
  if (opts.sub) { subEl.textContent = opts.sub; subEl.style.display = 'block'; }
  tblock.append(titleEl, subEl);

  const actions = document.createElement('div');
  actions.style.cssText = 'display:flex;align-items:center;gap:10px';
  const badgeSlot = document.createElement('div');
  badgeSlot.style.cssText = 'display:none';
  const closeHint = document.createElement('div');
  closeHint.style.cssText = `display:flex;align-items:center;gap:8px;color:${SK.faint};font-size:${SK.fsMicro}`;
  closeHint.innerHTML = `close ${kbd(opts.closeKey)}`;
  actions.append(badgeSlot, closeHint);
  header.append(tblock, actions);

  // BODY.
  const body = document.createElement('div');
  const pad = opts.bodyPad === false ? '0' : `${SHELL.pad}px`;
  body.style.cssText = `flex:1;min-height:0;overflow:hidden;padding:${pad}`;

  // FOOTER: hints left, close-key chip + word right. Hints never float over the world.
  const footer = document.createElement('div');
  footer.style.cssText = [
    'display:flex', 'align-items:center', 'justify-content:space-between', 'gap:16px', 'flex:none',
    `min-height:${SHELL.footMinH}px`, `padding:${SHELL.footPadV}px ${SHELL.pad}px`,
    `border-top:1px solid ${SK.border}`, 'background:rgba(0,0,0,0.18)',
  ].join(';');
  const hints = document.createElement('div');
  hints.style.cssText = `display:flex;align-items:center;flex-wrap:wrap;gap:10px;font-size:${SK.fsMicro};color:${SK.dim}`;
  if (opts.hintsHtml) hints.innerHTML = opts.hintsHtml;
  const footClose = document.createElement('div');
  footClose.style.cssText = `display:flex;align-items:center;gap:8px;color:${SK.faint};font-size:${SK.fsMicro}`;
  footClose.innerHTML = `${kbd(opts.closeKey)} close`;
  footer.append(hints, footClose);

  win.append(header, body, footer);
  scrim.appendChild(win);

  return {
    scrim, win, header, body, footer, hints,
    setTitle: (t) => { titleEl.textContent = t; },
    setSub: (s) => { if (s) { subEl.textContent = s; subEl.style.display = 'block'; } else subEl.style.display = 'none'; },
    setBadge: (html) => { if (html) { badgeSlot.innerHTML = html; badgeSlot.style.display = 'flex'; } else badgeSlot.style.display = 'none'; },
  };
}

// BODY LAYOUT B: two-pane split (inventory, machines, containers). LEFT fixed 480, RIGHT flex; each pane
// owns its own 24 padding, a 1px divider on the second pane. Call with bodyPad:false on the shell.
export function splitBody(shell: Shell): { left: HTMLDivElement; right: HTMLDivElement } {
  const grid = document.createElement('div');
  grid.style.cssText = `display:grid;grid-template-columns:${SHELL.paneLeft}px 1fr;height:100%;min-height:0`;
  const left = pane();
  const right = pane();
  right.style.borderLeft = `1px solid ${SK.border}`;
  grid.append(left, right);
  shell.body.appendChild(grid);
  return { left, right };
}

// BODY LAYOUT C: three columns (crafting only). 190 cats / 1fr list / 400 detail. Columns 2 and 3 get a
// left border. Each column padding 24. bodyPad:false on the shell.
export function threeCol(shell: Shell): { cats: HTMLDivElement; list: HTMLDivElement; detail: HTMLDivElement } {
  const grid = document.createElement('div');
  grid.style.cssText = `display:grid;grid-template-columns:${SHELL.colCats}px 1fr ${SHELL.colDetail}px;height:100%;min-height:0`;
  const cats = pane(); const list = pane(); const detail = pane();
  list.style.borderLeft = `1px solid ${SK.border}`;
  detail.style.borderLeft = `1px solid ${SK.border}`;
  grid.append(cats, list, detail);
  shell.body.appendChild(grid);
  return { cats, list, detail };
}

function pane(): HTMLDivElement {
  const p = document.createElement('div');
  p.style.cssText = `padding:${SHELL.pad}px;min-width:0;min-height:0;overflow:auto;display:flex;flex-direction:column`;
  return p;
}

// A pane header: label left (.ox-label), optional meta right, 16px margin-bottom (spec).
export function paneHead(label: string, metaHtml?: string): HTMLDivElement {
  const h = document.createElement('div');
  h.style.cssText = `display:flex;align-items:center;justify-content:space-between;gap:12px;margin-bottom:${SHELL.gap}px;flex:none`;
  const l = document.createElement('div'); l.textContent = label.toUpperCase(); l.style.cssText = labelCss();
  h.appendChild(l);
  if (metaHtml != null) { const m = document.createElement('div'); m.style.cssText = `font-size:${SK.fsMicro};color:${SK.faint}`; m.innerHTML = metaHtml; h.appendChild(m); }
  return h;
}

// A standard 64/72/56 slot (spec slot spec). kind picks the size; every slot is radius md, gap 8 handled
// by the container. Returns the empty cell; callers fill icon/count/labels.
export function slotEl(kind: 'grid' | 'hotbar' | 'queue' = 'grid'): HTMLDivElement {
  const size = kind === 'hotbar' ? 72 : kind === 'queue' ? 56 : 64;
  const cell = document.createElement('div');
  cell.style.cssText = [
    'position:relative', `width:${size}px`, `height:${size}px`, 'flex:none', `border-radius:${SK.rMd}`,
    `background:${SK.raised}`, `border:1px solid ${SK.border}`, 'overflow:hidden', 'user-select:none',
    `transition:border-color ${SK.tFast} ${SK.ease},background ${SK.tFast} ${SK.ease}`,
  ].join(';');
  return cell;
}

export const SHELL_DIMS = SHELL;
